// 任务表的列定义（任务列表）：编号、标题（客户·UEN 两行）、类型、状态、进度（一句话 + 看画面）、耗时、创建（相对时间）、优先级
import { Link } from "react-router-dom";
import type { TaskDTO, TaskStatus } from "@csp/shared";
import { fmtDateTime, fmtTime, timingShort } from "../lib/format";
import { DryBadge, PriorityBadge, StatusBadge } from "./Badges";
import type { Column } from "./DataTable";
import { LiveButton, progressSentence } from "./LiveButton";

const SHOW_PROGRESS: TaskStatus[] = ["running", "waiting_scan", "review_required", "blocked"];

export const TASK_COLUMNS: Column<TaskDTO>[] = [
  { key: "no", label: "编号", fixed: true, render: (t) => <span className="muted">#{t.no}</span> },
  {
    key: "title",
    label: "标题",
    fixed: true,
    render: (t) => (
      <div className="cell-two">
        <b title={t.title} translate="no">
          <Link to={`/tasks/${t.id}`} className="plain" onClick={(e) => e.stopPropagation()}>
            {t.title}
          </Link>
          {t.dryRun && (
            <>
              {" "}
              <DryBadge />
            </>
          )}
        </b>
        <span title={t.clientEntity.name} translate={t.clientEntity.name ? "no" : undefined}>
          {t.clientEntity.name || "（未填公司名）"}
          {t.clientEntity.uen && ` · UEN ${t.clientEntity.uen}`}
        </span>
        {t.browserKind === "corppass" && <span className={`small ${t.assigneeLabel ? "" : "err"}`}>扫码人：{t.assigneeLabel ?? "未分配"}</span>}
      </div>
    ),
  },
  { key: "type", label: "类型", render: (t) => t.typeName },
  {
    key: "status",
    label: "状态",
    fixed: true,
    tone: (t) => (t.status === "blocked" ? "cell-alert" : undefined),
    render: (t) => (
      <>
        <StatusBadge status={t.status} />
        {t.status === "blocked" && (
          <span className="muted small" title={`卡住于 ${fmtDateTime(t.statusSince)}`} data-testid="stuck-since">
            {" "}
            {fmtTime(t.statusSince)}
          </span>
        )}
      </>
    ),
  },
  {
    key: "progress",
    label: "进度",
    render: (t) =>
      SHOW_PROGRESS.includes(t.status) || t.status === "queued" ? (
        <div className="cell-progress">
          <span className="cell-clip" title={progressSentence(t)} data-testid="progress-sentence">
            {t.progress && t.status !== "queued" && <span className="muted">{t.progress.k}/{t.progress.n} · </span>}
            <span translate="no">{progressSentence(t)}</span>
          </span>
          <LiveButton task={t} />
        </div>
      ) : t.status === "draft" && t.missing.length ? (
        <span className="err">缺 {t.missing.length} 项材料</span>
      ) : t.status === "done" && t.result?.receiptNo ? (
        <span>回执 {t.result.receiptNo}</span>
      ) : (
        <span className="muted">—</span>
      ),
  },
  {
    key: "timing",
    label: "耗时",
    render: (t) => {
      const s = t.timing ? timingShort(t.timing, t.status) : "";
      return s ? <span data-testid="timing">{s}</span> : <span className="muted">—</span>;
    },
  },
  {
    key: "created",
    label: "创建",
    render: (t) => (
      <span title={fmtDateTime(t.createdAt)}>
        <span translate="no">{t.createdBy}</span> <span className="muted">· {fmtTime(t.createdAt)}</span>
      </span>
    ),
  },
  {
    key: "priority",
    label: "优先级",
    render: (t) => (t.priority === "normal" ? <span className="muted">普通</span> : <PriorityBadge priority={t.priority} />),
  },
];
